import { watch, onUnmounted } from 'vue'
import { useConfigStore } from '@/stores/ConfigStore'
import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useRoute } from 'vue-router'
import { updateCelloPosition } from '@/hooks/useCello'

const HIDDEN_ROUTES = [
  'MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted',
  'SignIn', 'SignUp', 'SignUpEmail', 'ResetPassword', 'SetPassword', 'EmailSent', 'LinkExpired',
]

let _observer = null
let _bottom   = '18px'
let _right    = '18px'
let _hidden   = false
let _booted   = false

const getHost = () => document.getElementById('cdp-support-center')
const getBtn  = () => getHost()?.shadowRoot?.querySelector('#cdp-widget-root button')

const clickWhenReady = (attempts = 20) => {
  const btn = getBtn()
  if (btn) { btn.click(); return }
  if (attempts > 0) setTimeout(() => clickWhenReady(attempts - 1), 100)
}

// Inject a <style> directly into the shadow root — overrides Tailwind's bottom-5/right-5
// via CSS cascade before the browser paints, eliminating any position flash.
const injectShadowCss = () => {
  const host = getHost()
  if (!host?.shadowRoot) return

  let style = host.shadowRoot.getElementById('cdp-pos-override')
  if (!style) {
    style = document.createElement('style')
    style.id = 'cdp-pos-override'
    host.shadowRoot.insertBefore(style, host.shadowRoot.firstChild)
  }

  if (_hidden) {
    // :host targets the shadow host from inside — the widget cannot override
    // its own injected shadow stylesheet, so this reliably wins over any
    // inline style the widget sets on the host element.
    style.textContent = `:host { display: none !important; }`
  } else {
    // Panel sits 76px above the button (bottom-24 vs bottom-5 = 96px - 20px gap).
    // Both elements use fixed positioning with hardcoded Tailwind right-5 classes —
    // we override both so the panel stays aligned with the button when repositioned.
    const panelBottom = `${parseInt(_bottom) + 76}px`
    style.textContent = `
      :host { display: block !important; }
      button[class*="z-[9999]"] { bottom: ${_bottom} !important; right: ${_right} !important; }
      div[class*="z-[9998]"]    { bottom: ${panelBottom} !important; right: ${_right} !important; background-color: #ffffff !important; }
    `
  }
}

const applyStyle = () => {
  const host = getHost()
  if (!host) return
  injectShadowCss()
}

// Observe only direct childList changes on the shadow root — fires if the widget
// completely recreates its DOM (e.g. re-init), NOT on every internal animation
// or state update. This avoids the tight-loop that subtree+attributes caused.
const startObserver = () => {
  const host = getHost()
  if (!host?.shadowRoot || _observer) return
  _observer = new MutationObserver(() => injectShadowCss())
  _observer.observe(host.shadowRoot, { childList: true })
}

const setPosition = (bottom, right) => {
  _bottom = bottom
  _right  = right
  _hidden = false
  applyStyle()
  updateCelloPosition(bottom, right)
}

const hide = () => {
  _hidden = true
  applyStyle()
}

export const useCdpSupport = () => {
  const configStore = useConfigStore()
  const userStore   = useUserStore()
  const globalStore = useGlobalStore()
  const route       = useRoute()

  const updatePosition = () => {
    const isMobile = window.innerWidth < 768
    const isMobileOrTablet = window.innerWidth < 1280

    // `fullScreen` routes (e.g. the pick-list print view) render without app
    // chrome — keep the floating support widget off them too. Also hide while a
    // date-range calendar is open so the bubble can't overlap it.
    if (
      HIDDEN_ROUTES.includes(route.name)
      || route.meta?.fullScreen
      || globalStore.datePickerOpen
      || (isMobile && globalStore.sidebar)
      || isMobileOrTablet
    ) {
      hide()
      return
    }

    // When an AppDetailPanel is open the chat bubble would sit on top of the
    // panel's footer buttons (CDP-2281). Shift the widget left by the panel
    // width + a 12px gap so it clears the panel. The 8px floor avoids
    // pinning the bubble flush against the viewport edge on narrow screens.
    const panelOffset = globalStore.detailPanelOpen
      ? Math.max(globalStore.detailPanelWidth + 12, 8)
      : 0
    const withPanel = (right) => `${parseInt(right) + panelOffset}px`

    // AppPagination in fixed mode publishes its height here. Lift the bubble
    // by that height + a 12px gap so it never sits under the bar. 0 when no
    // bar is mounted.
    const barLift = globalStore.paginationBarHeight > 0
      ? globalStore.paginationBarHeight + 12
      : 0
    const withBar = (bottom) => `${parseInt(bottom) + barLift}px`

    if (['/batches', '/inventory'].includes(route.path)) {
      setPosition(
        withBar(isMobile ? '70px' : '55px'),
        withPanel(globalStore.videoWidgetOpen ? '380px' : (isMobile ? '18px' : '75px'))
      )
    } else if (route.path.includes('history')) {
      setPosition(
        withBar(isMobile ? '65px' : '50px'),
        withPanel(globalStore.videoWidgetOpen ? '380px' : '18px')
      )
    } else {
      setPosition(withBar('18px'), withPanel(globalStore.videoWidgetOpen ? '380px' : '18px'))
    }
  }

  const boot = (token) => {
    if (_booted) return
    _booted = true

    const config = {
      apiUrl:       'https://support.carddealerpro.com/api',
      title:        'CDP Support',
      primaryColor: '#0086FF',
      guestMode:    false,
      pageContext:  {
        plan: configStore.config?.subscription?.plan_id ?? null,
      },
      user: {
        email:  userStore.user.email,
        userId: String(userStore.user.id),
        name:   [userStore.user.first_name, userStore.user.last_name].filter(Boolean).join(' '),
      },
    }
    
    // Logged-out: omit guestMode entirely so the widget shows its own sign-in/guest form

    const afterInit = () => {
      requestAnimationFrame(() => {
        startObserver()
        updatePosition()
        if (route.query.cdpSupport) {
          clickWhenReady()
        }
      })
    }

    if (window.CDPSupport) {
      window.CDPSupport.init(config)
      afterInit()
      return
    }

    const script = document.querySelector('script[src*="cdpsupport.replit.app"]')
    script?.addEventListener('load', () => {
      window.CDPSupport?.init(config)
      afterInit()
    }, { once: true })
  }

  watch(() => configStore.config, (config) => {
    if (!config || !Object.keys(config).length) return
    boot(config.support_token ?? null)
  }, { immediate: true })

  watch(route, (r) => {
    updatePosition()
    if (r.query.cdpSupport) getHost()?.shadowRoot?.querySelector('#cdp-widget-root button')?.click()
  })
  watch(() => globalStore.videoWidgetOpen, updatePosition)
  watch(() => globalStore.sidebar, updatePosition)
  // Both flags shift the widget — they change together when a panel opens
  // but watching the width too covers the (unlikely) future case of a panel
  // whose width changes while it's already open.
  watch(() => globalStore.detailPanelOpen,  updatePosition)
  watch(() => globalStore.detailPanelWidth, updatePosition)
  watch(() => globalStore.paginationBarHeight, updatePosition)
  watch(() => globalStore.datePickerOpen, updatePosition)

  const onResize = () => updatePosition()
  window.addEventListener('resize', onResize)
  onUnmounted(() => window.removeEventListener('resize', onResize))

  // Re-boot when auth state changes so identity is always current
  watch(() => userStore.user, (user, prev) => {
    if (!user) {
      // Logged out — re-init anonymously
      _booted = false
      boot(null)
    } else if (!prev) {
      // Just logged in — re-init with identity
      _booted = false
      boot(configStore.config.support_token ?? null)
    }
  })
}
